<!--
  A panel that opens from a header item (`anchor`): placed below it (above
  when there is no room), kept inside the viewport, following it as the
  page scrolls. Escape closes it and gives the focus back to the anchor;
  so does a click outside, without moving the focus. As a `menu`, the
  arrow keys, Home and End move between its `[role="menuitem"]` items and
  Tab closes it; any panel closes when the focus moves out of it into the
  page. It takes the focus when it opens: the item `initial` selects, else
  the element marked `data-autofocus`, else its first item, or itself. It
  is never taller than the room on the side it opens on, and scrolls inside.
-->
<script lang="ts">
  import { flushSync, onMount, tick, type Snippet } from 'svelte';

  interface Props {
    anchor: HTMLElement;
    /** `group`: a panel that is neither, named by `label` (a picker's list with its search box). */
    role: 'menu' | 'dialog' | 'group';
    label: string;
    id?: string;
    /** A selector for the item that takes the focus when the panel opens, when there is one. */
    initial?: string;
    onclose: (refocus: boolean) => void;
    children: Snippet;
  }
  let { anchor, role, label, id, initial, onclose, children }: Props = $props();

  let pop: HTMLElement | null = $state(null);
  let pos = $state({ top: 0, left: 0, max: 560 });

  /** Below the anchor, or above when there is more room there, kept inside the viewport (16px sides, 8px ends). */
  function place() {
    if (!pop) return;
    const r = anchor.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = Math.min(pop.scrollHeight, 560);
    const left = Math.max(16, Math.min(r.left, innerWidth - w - 16));
    const below = Math.max(0, innerHeight - (r.bottom + 6) - 8);
    const above = Math.max(0, r.top - 6 - 8);
    if (h <= below || below >= above) pos = { top: r.bottom + 6, left, max: Math.min(560, below) };
    else pos = { top: r.top - 6 - Math.min(h, above), left, max: Math.min(560, above) };
  }

  /** The focus left the panel for somewhere else in the page (not its anchor): it is done. */
  function focusout(e: FocusEvent) {
    const to = e.relatedTarget as Node | null;
    if (to && !pop?.contains(to) && !anchor.contains(to)) onclose(false);
  }

  onMount(() => {
    void tick().then(() => {
      place();
      // Its place and height are in the DOM before the focus is: an item scrolled to is scrolled within the panel as shown.
      flushSync();
      const first =
        (initial ? pop?.querySelector<HTMLElement>(initial) : null) ??
        pop?.querySelector<HTMLElement>('[data-autofocus]') ??
        pop?.querySelector<HTMLElement>('[role="menuitem"], a[href], button') ??
        pop;
      first?.focus();
      // The focused item may be below the fold of a long panel.
      if (first !== pop) first?.scrollIntoView?.({ block: 'nearest' });
    });
    const onScroll = () => place();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!pop?.contains(t) && !anchor.contains(t)) onclose(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('pointerdown', onDown);
    };
  });

  function keydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onclose(true);
      return;
    }
    if (role !== 'menu') return;
    const items = [...(pop?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => items[(i + items.length) % items.length]?.focus();
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') {
      onclose(false);
      return;
    } else return;
    e.preventDefault();
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div bind:this={pop} class="popover" {role} {id} aria-label={label} tabindex="-1" style:top="{pos.top}px" style:left="{pos.left}px" style:max-height="{pos.max}px" onkeydown={keydown} onfocusout={focusout}>
  {@render children()}
</div>

<style>
  .popover {
    position: fixed;
    z-index: 60;
    max-width: calc(100vw - 32px);
    overflow: auto;
    background: var(--surface);
    color: var(--fg);
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 12px 32px #0000002e;
    padding: 6px;
    font-size: 13px;
    text-transform: none;
    letter-spacing: normal;
  }
  .popover:focus {
    outline: none;
  }
</style>
